import { AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import ApiPath from "src/api/apiPath.json";
import { ApiResponse } from "../public-types";
import { MbOrg, MbOrgDetail, OrgAdminMemoListParams, OrgAdminMemoListResponse, RequestInfo, SendBody } from "./org-types";

/**
 * id 조건에서 유효한 조직 id 만 추출 (null / undefined / 0 / 빈값 / 숫자 아닌 값 제외)
 * - id 는 "1,2,3" 형태의 복수 전달을 허용하므로 요소 단위로 걸러낸다
 */
const filterValidOrgIds = (id?: RequestInfo["id"]): string[] =>
  String(id ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => Number(value) > 0);

/** RMB13 */
const getOrgList = (axios: AxiosInstance, params?: RequestInfo): Promise<AxiosResponse<ApiResponse<{ content: MbOrg[] }>>> => {
  const path = ApiPath.organization.list;

  // id 조건 조회인데 유효한 id 가 하나도 없으면(예: ?id=0) 서버 호출 없이 빈 목록을 반환한다
  if (params && "id" in params) {
    const validOrgIds = filterValidOrgIds(params.id);
    if (validOrgIds.length === 0) {
      return Promise.resolve({
        data: { data: { content: [] }, meta: {} },
        status: 200,
        statusText: "OK",
        headers: {},
        config: {} as any,
      } as AxiosResponse<ApiResponse<{ content: MbOrg[] }>>);
    }
    params = { ...params, id: validOrgIds.join(",") };
  }

  return axios.get<ApiResponse<{ content: MbOrg[] }>>(path, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
};

const getOrgDetail = (
  axios: AxiosInstance,
  params?: {
    id: number;
  },
): Promise<AxiosResponse<ApiResponse<MbOrgDetail>>> => {
  const path = ApiPath.organization.detail.replace("{id}", params!.id!.toString());
  return axios.get<ApiResponse<MbOrgDetail>>(path, { params });
};

interface FileData {
  fileStorageType: string;
  key: string;
  filename: string;
  contentType: string;
  filesize: number;
}

// public 업록드
async function uploadOrgPublicFilesAsync(axios: AxiosInstance, param: any): Promise<AxiosResponse<ApiResponse<{ media: FileData }>>> {
  const path = ApiPath.file.upload.replace("{type}", "public");
  return await axios.post(path, param);
}

// private 업로드
async function uploadOrgPrivateFilesAsync(axios: AxiosInstance, param: any): Promise<AxiosResponse<ApiResponse<{ media: FileData }>>> {
  const path = ApiPath.file.upload.replace("{type}", "private");
  return await axios.post(path, param);
}

const addOrg = (axios: AxiosInstance, params?: SendBody): Promise<AxiosResponse<ApiResponse<MbOrgDetail>>> => {
  const path = ApiPath.organization.add;
  return axios.post<ApiResponse<MbOrgDetail>>(path, params);
};

const editOrg = (axios: AxiosInstance, params?: SendBody): Promise<AxiosResponse<ApiResponse<MbOrgDetail>>> => {
  let path = "";
  if (params && params.organization.id) {
    path = ApiPath.organization.edit.replace("{id}", params?.organization?.id?.toString());
  }

  return axios.put<ApiResponse<MbOrgDetail>>(path, params);
};

const proofFileDownload = (
  axios: AxiosInstance,
  params?: {
    url: string;
  },
): Promise<AxiosResponse<ApiResponse<any>>> => {
  return axios.get<ApiResponse<any>>(params!.url);
};

/** 사업자 관리자메모 목록 api*/
export const getOrgAdminMemoAsync = async (
  axios: AxiosInstance,
  params?: {
    orgId: number;
    queryParams?: OrgAdminMemoListParams;
  },
): Promise<AxiosResponse<ApiResponse<OrgAdminMemoListResponse>>> => {
  return await axios.get(ApiPath.organization.adminMemo.list.replace("{orgId}", String(params?.orgId)), {
    params: params?.queryParams,
    paramsSerializer: {
      serialize: (param) => qs.stringify(param, { allowDots: true, encode: true }),
    },
  });
};

/** 사업자 관리자메모 등록 api*/

export const postOrgAdminMemoAsync = async (
  axios: AxiosInstance,
  params?: {
    orgId: number;
    content: string;
  },
) => {
  return await axios.post(ApiPath.organization.adminMemo.add.replace("{orgId}", String(params?.orgId)), {
    content: params?.content,
  });
};

/** 사업자 관리자메모 수정 api*/
export const editOrgAdminMemoAsync = async (
  axios: AxiosInstance,
  params?: {
    orgId: number;
    memoId: number;
    content: string;
  },
) => {
  const editOrgMemo = ApiPath.organization.adminMemo.edit.replace("{orgId}", String(params?.orgId));
  const path = editOrgMemo.replace("{id}", String(params?.memoId));
  return await axios.put(path, {
    content: params?.content,
  });
};

/** 사업자 관리자메모 삭제 api*/
export const removeOrgAdminMemoAsync = async (
  axios: AxiosInstance,
  params?: {
    orgId: number;
    memoId: number;
  },
): Promise<AxiosResponse<ApiResponse<OrgAdminMemoListResponse>>> => {
  const editOrgMemo = ApiPath.organization.adminMemo.delete.replace("{orgId}", String(params?.orgId));
  const path = editOrgMemo.replace("{id}", String(params?.memoId));

  return await axios.delete(path);
};

export { addOrg, editOrg, getOrgDetail, getOrgList, proofFileDownload, uploadOrgPrivateFilesAsync, uploadOrgPublicFilesAsync };
